import StringUtils from "../utils/StringUtils.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import BaseGame from "./BaseGame.js";

const FLAG_HARD_CONFIG = {
	passportStamp: "flag",
	gameNameKey: "home.flagNerd",
	modeLabelKey: "home.hard",
	mode: "hard",
	shareEmoji: "🟥",
	slug: "flag-nerd-hard"
};

/**
 * Mode tapé : le joueur écrit le nom du pays. Sert aussi de base à Shape Nerd
 * Hard, qui remplace le drapeau par une silhouette (`getQuestionFlagCodes()`,
 * `renderQuestion()`).
 */
export default class FlagNerdHard extends BaseGame {
	constructor(gameId = 'flagnerdhard', pageSelector = '.flag-nerd-hard', config = FLAG_HARD_CONFIG) {
		super(gameId, pageSelector, config);
		if (!this.page) return;

		this.answerInput = this.answerContainer.querySelector(".answer-input");
		this.validateButton = this.answerContainer.querySelector(".validate-input");
		this.answerReveal = this.answerContainer.querySelector(".answer-reveal");
		this.buildEvents();
		this.init();
	}

	newProposal() {
		super.newProposal();
		if (this.countriesLeft.length === 0) return;

		this.canGuess = false;
		this.whenFlagsReady(this.getQuestionFlagCodes(), () => {
			this.canGuess = true;
			this.flagContainer.innerHTML = this.renderQuestion(this.rightAnswer);
			this.answerInput.focus();
		});
	}

	/** Drapeaux à avoir en cache avant d'afficher la question. */
	getQuestionFlagCodes() {
		return [this.rightAnswer.code];
	}

	renderQuestion(country) {
		return `<div class="flag" style="background-image: url(${getFlagPath(country.code)})"></div>`;
	}

	buildEvents() {
		this.answerInput.addEventListener("keydown", e => {
			// preventDefault stops the mobile browser's default action on the
			// virtual keyboard's Enter/Go key (which otherwise blurs the field
			// and closes the keyboard).
			if (e.key === "Enter") {
				e.preventDefault();
				this.validateAnswer(this.answerInput.value);
				this.keepKeyboardOpen();
			}
		});
		const submit = () => {
			this.validateAnswer(this.answerInput.value);
			this.keepKeyboardOpen();
		};
		// Act on touchstart (the earliest touch event) + preventDefault. iOS
		// "steals" the first tap on a control while the keyboard is open to
		// dismiss it — firing touchcancel instead of click/touchend, so those
		// handlers never run. touchstart fires before that, and preventDefault
		// keeps focus on the input (keyboard stays) and suppresses the emulated
		// click (no double-submit). click stays for desktop / non-touch.
		this.validateButton.addEventListener("touchstart", e => { e.preventDefault(); submit(); }, { passive: false });
		this.validateButton.addEventListener("click", submit);
	}

	/**
	 * Donne le nom attendu au moment de la faute. Sans cela, se tromper en mode
	 * tapé n'apprenait rien : le champ virait au rouge et la question suivante
	 * arrivait, le drapeau qu'on venait de regarder restant sans réponse jusqu'au
	 * récap de fin de partie.
	 */
	revealAnswer() {
		if (!this.answerReveal) return;
		const lang = geoNerdApp.languageManager;
		this.answerReveal.innerHTML = `${lang.t("game.answerWas")} <strong>${this.rightAnswer.name}</strong>`;
		this.answerReveal.classList.add("show");
	}

	hideAnswer() {
		if (!this.answerReveal) return;
		this.answerReveal.classList.remove("show");
		this.answerReveal.innerHTML = "";
	}

	// Re-focus the input synchronously, inside the user gesture, so the mobile
	// virtual keyboard stays/re-opens between guesses (Android closes it on the
	// keyboard's Enter/Go key even with preventDefault). No-op once the run is
	// over (input is about to be removed).
	keepKeyboardOpen() {
		if (this.lifes > 0 && this.countriesLeft.length > 0) {
			this.answerInput.focus({ preventScroll: true });
		}
	}

	validateAnswer(answer) {
		if (!this.canGuess) return;
		// Valider à vide (Entrée réflexe, second appui après avoir effacé) ne coûte
		// rien : sans réponse il n'y a pas de faute à sanctionner, et une vie perdue
		// sur un champ vide est incompréhensible pour le joueur.
		if (!answer || !answer.trim()) return;
		this.canGuess = false;

		const isCorrect = StringUtils.matchesAnswer(answer, this.rightAnswer, geoNerdApp.countries);

		const feedbackColor = isCorrect ? "#33ce29" : "#F05050";

		gsap.to(this.answerInput, {
			backgroundColor: feedbackColor,
			color: "white",
			duration: 0.3,
			onComplete: () => {
				gsap.set(this.answerInput, {
					backgroundColor: "white",
					color: "black",
					delay: 0.8
				});
			}
		});

		if (isCorrect) {
			SoundManager.correct();
			FeedbackEffects.confetti(this.answerInput);
			this.markFound();
		} else {
			SoundManager.wrong();
			FeedbackEffects.shake(this.page.querySelector(".game-container"));
			this.revealAnswer();
			this.markWrong({
				countryName: this.rightAnswer.name,
				correctCode: this.rightAnswer.code,
				guessedText: answer
			});
		}

		this.advanceTurn(() => { this.answerInput.value = ""; this.hideAnswer(); });
	}

	renderErrorItem(error) {
		return `
			<div class="error-item">
				<div class="error-flag correct">
					<img src="${getFlagPath(error.correctCode)}" alt="${error.countryName}">
				</div>
				<div class="error-details">
					<div class="error-country">${error.countryName}</div>
					<div class="error-answer-text wrong">${error.guessedText}</div>
				</div>
			</div>
		`;
	}
}
